import api, { API_BASE_URL } from '@/shared/api';
import { buildFormData, type ApiResponse } from '@/shared/lib';
import type {
  CreateGeneratedRecordPayload,
  CreateGeneratedRecordResponsePayload,
  EditGeneratedRecordPayload,
  GeneratedRecordForm,
  GeneratedRecordHistory,
  GeneratedRecordListPage,
  GeneratedRecordSaveResult,
  GeneratedRecordStage,
  ListGeneratedRecordsParams,
  OpenGeneratedRecordResult,
  ReleaseGeneratedRecordPayload,
} from '../model/generated-record.types';

// `identifier` is the serial number (IF) or the service order (OS) the ERP is asked about; the
// other record types are refused when they send one.
export async function getGenerationForm(
  matrixId: number,
  { identifier, indexer }: { identifier?: string; indexer?: string } = {}
): Promise<GeneratedRecordForm> {
  const response = await api.get<ApiResponse<GeneratedRecordForm>>('/generated-records/form', {
    params: {
      matrixId,
      ...(identifier === undefined ? {} : { identifier }),
      ...(indexer === undefined ? {} : { indexer }),
    },
  });
  const form = response.data.data;

  if (form === undefined) {
    throw new Error('Formulário do Registro indisponível.');
  }

  return form;
}

export async function createGeneratedRecord(
  payload: CreateGeneratedRecordPayload
): Promise<GeneratedRecordSaveResult> {
  const response = await api.post<ApiResponse<GeneratedRecordSaveResult>>(
    '/generated-records',
    payload
  );
  const result = response.data.data;

  if (result === undefined) {
    throw new Error('Resposta do servidor sem o Registro criado.');
  }

  return result;
}

export async function listGeneratedRecords(
  params: ListGeneratedRecordsParams
): Promise<GeneratedRecordListPage> {
  const response = await api.get<GeneratedRecordListPage>('/generated-records', { params });

  return response.data;
}

export async function listGeneratedRecordStages(prefix: string): Promise<GeneratedRecordStage[]> {
  const response = await api.get<ApiResponse<GeneratedRecordStage[]>>('/generated-records/stages', {
    params: { prefix },
  });

  return response.data.data ?? [];
}

// Reads the record without taking its lock.
export async function getGeneratedRecordForm(
  id: number,
  { isCorrection = false }: { isCorrection?: boolean } = {}
): Promise<GeneratedRecordForm> {
  const url = `/generated-records/${id}/form`;
  const response = isCorrection
    ? await api.get<ApiResponse<GeneratedRecordForm>>(url, { params: { isCorrection: true } })
    : await api.get<ApiResponse<GeneratedRecordForm>>(url);
  const form = response.data.data;

  if (form === undefined) {
    throw new Error('Formulário do Registro indisponível.');
  }

  return form;
}

export async function getGeneratedRecordHistory(id: number): Promise<GeneratedRecordHistory> {
  const response = await api.get<ApiResponse<GeneratedRecordHistory>>(
    `/generated-records/${id}/history`
  );
  const history = response.data.data;

  if (history === undefined) {
    throw new Error('Logs de preenchimento indisponíveis.');
  }

  return history;
}

export async function openGeneratedRecord(id: number): Promise<OpenGeneratedRecordResult> {
  const response = await api.post<ApiResponse<OpenGeneratedRecordResult>>(
    `/generated-records/${id}/open`
  );
  const opened = response.data.data;

  if (opened === undefined) {
    throw new Error('Resposta do servidor sem o Registro aberto.');
  }

  return opened;
}

export async function respondGeneratedRecord(
  id: number,
  payload: CreateGeneratedRecordResponsePayload
): Promise<GeneratedRecordSaveResult> {
  const response = await api.post<ApiResponse<GeneratedRecordSaveResult>>(
    `/generated-records/${id}/responses`,
    payload
  );
  const result = response.data.data;

  if (result === undefined) {
    throw new Error('Resposta do servidor sem a resposta gravada.');
  }

  return result;
}

// Neither route takes the lock: the server numbers a PDF response under the row lock, and the sheet
// edit of an Upload record checks `baseResponseNumber` instead of a lock token.
export async function uploadGeneratedRecordPdf(
  id: number,
  file: File,
  stageId: number
): Promise<GeneratedRecordSaveResult> {
  const response = await api.post<ApiResponse<GeneratedRecordSaveResult>>(
    `/generated-records/${id}/pdf-responses`,
    buildFormData({ file, stageId }),
    { headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' } }
  );
  const result = response.data.data;

  if (result === undefined) {
    throw new Error('Resposta do servidor sem a resposta gravada.');
  }

  return result;
}

export async function editGeneratedRecord(
  id: number,
  payload: EditGeneratedRecordPayload
): Promise<GeneratedRecordSaveResult> {
  const response = await api.post<ApiResponse<GeneratedRecordSaveResult>>(
    `/generated-records/${id}/edit-responses`,
    payload
  );
  const result = response.data.data;

  if (result === undefined) {
    throw new Error('Resposta do servidor sem a resposta gravada.');
  }

  return result;
}

export async function getGeneratedRecordResponsePdf(id: number, number: number): Promise<Blob> {
  const response = await api.get<Blob>(`/generated-records/${id}/responses/${number}/pdf`, {
    responseType: 'blob',
  });

  return response.data;
}

export async function releaseGeneratedRecord(
  id: number,
  payload: ReleaseGeneratedRecordPayload
): Promise<void> {
  await api.post(`/generated-records/${id}/release`, payload);
}

// Plain `fetch` with `keepalive`, never axios: the browser cancels ordinary requests while the page
// goes away, and only a keepalive request outlives the document. `sendBeacon` cannot carry the
// session cookie to the API when it lives on another origin.
export function releaseGeneratedRecordOnUnload(
  id: number,
  payload: ReleaseGeneratedRecordPayload
): void {
  try {
    fetch(`${API_BASE_URL}/generated-records/${id}/release`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => undefined);
  } catch {
    // An unloading page has nowhere to report a failure.
  }
}
